import React from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { DeltaPill } from '../../components/primitives/DeltaPill';
import { formatChangePct, formatStreamingNumber } from '../streamingFormat';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import styles from '../Streaming.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface RankedRowProps {
  rank: number;
  name: React.ReactNode;
  /** 0 to 1: the share bar. Null draws no bar. */
  share: number | null;
  value: number | null;
  changePct: number | null;
  isActive?: boolean;
  onOpen?: () => void;
}

/** One row of a ranked list: rank, name, share bar, number and change (design c1, c2). */
const RankedRowComponent: React.FC<RankedRowProps> = ({ rank, name, share, value, changePct, isActive, onOpen }) => {
  const intl = useIntl();
  const change = formatChangePct(intl, changePct);
  const body = (
    <>
      <span className={styles.rank}>{rank}</span>
      <span className={styles.rankName}>{name}</span>
      <span className={styles.share} aria-hidden>
        {share === null ? null : (
          <span className={styles.shareFill} style={{ width: `${Math.round(Math.min(1, share) * 100)}%` }} />
        )}
      </span>
      <span className={styles.num}>{formatStreamingNumber(intl, value)}</span>
      <span className={styles.num}>
        {change !== null && changePct !== null ? <DeltaPill value={changePct} label={change} /> : '—'}
      </span>
    </>
  );
  return (
    <li>
      {onOpen === undefined ? (
        <div className={styles.rankRow}>{body}</div>
      ) : (
        <button
          type="button"
          className={clsx(styles.rankRow, isActive === true && styles.rankOn)}
          aria-current={isActive === true ? 'true' : undefined}
          onClick={onOpen}
        >
          {body}
        </button>
      )}
    </li>
  );
};

export const RankedRow = TypedMemo(RankedRowComponent);
